// src/components/editor/PublishModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE } from '../../utils/api';

// Inline style for modal appearance
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxWidth: '90vw',
  backgroundColor: 'white',
  borderRadius: 8,
  boxShadow: 24,
  padding: 12
};

/**
 * PublishModal
 * ------------
 * Pure UI component that gathers metadata and calls `onConfirm(formData)`.
 * It does NOT talk to the backend itself (prevents double requests / 401).
 * Parent (EditorPage) performs the API call with the correct headers.
 */
const PublishModal = ({ open, onClose, onConfirm, doc }) => {
  const [formData, setFormData] = useState({
    title: '',
    volume: '',
    authorName: '',
    coAuthorEmail: '',
    genre: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill when doc changes
  useEffect(() => {
    if (doc) {
      setFormData({
        title: doc.title || '',
        volume: doc.volume || '',
        authorName: doc.authorName || '',
        coAuthorEmail: doc.coAuthorEmail || '',
        genre: doc.genre || ''
      });
    }
  }, [doc]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);

      await onConfirm({
        ...formData,
        title: (formData.title || '').trim(),
        authorName: (formData.authorName || '').trim(),
        coAuthorEmail: (formData.coAuthorEmail || '').trim(),
        genre: (formData.genre || '').trim(),
        volume: (formData.volume || '').toString().trim()
        // IMPORTANT: do NOT send pdfFileName — server will enforce canonical name
      });
      // Parent will close modal and navigate on success
    } catch (err) {
      console.error('Submit error (modal):', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreviewPdf = () => {
    if (!doc?._id) return;
    // Simple browser preview for the current draft PDF
    window.open(
      `${API_BASE}/api/drafts/${doc._id}/pdf?preview=1`,
      '_blank',
      'noopener,noreferrer'
    );
  };
  
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Publish Book</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Title */}
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          margin="normal"
        />

        {/* Volume */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Volume</InputLabel>
          <Select
            name="volume"
            value={formData.volume}
            onChange={handleChange}
            label="Volume"
          >
            {[...Array(20)].map((_, i) => (
              <MenuItem key={i + 1} value={(i + 1).toString()}>
                {i + 1}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Author Name */}
        <TextField
          fullWidth
          label="Author Name"
          name="authorName"
          value={formData.authorName}
          onChange={handleChange}
          margin="normal"
        />

        {/* Co-author Email */}
        <TextField
          fullWidth
          label="Co-author Email"
          name="coAuthorEmail"
          value={formData.coAuthorEmail}
          onChange={handleChange}
          margin="normal"
        />

        {/* Genre */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Genre</InputLabel>
          <Select
            name="genre"
            value={formData.genre}
            onChange={handleChange}
            label="Genre"
          >
            <MenuItem value="fiction">Fiction</MenuItem>
            <MenuItem value="non-fiction">Non-Fiction</MenuItem>
            <MenuItem value="academic">Academic</MenuItem>
          </Select>
        </FormControl>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Send for Review'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PublishModal;
